import { isLoggedInUser } from '../utils/authUtils';
import {
  getEncryptedAccountNum,
  getSavedButtonsCount,
  getUserType,
  isDonateButton,
  isHostedFlow,
} from '../utils/buttonFactoryUtils';

const count = getSavedButtonsCount();
const state = count > 1 ? 'populated' : 'empty';
const isHosted = isHostedFlow();

const PAGE_SPEC = {
  LANDING_PAGE: {
    pgrp: 'main:wps:donate:landing:login',
    page: 'main:wps:donate:landing:login',
  },
  MANAGE_PAGE: {
    pgrp: 'main:wps:donate:button_manager',
    page: 'main:wps:donate:button_manager',
  },
  OVERVIEW_PAGE: {
    pgrp: 'main:wps:donate:button_overview',
    page: 'main:wps:donate:button_overview',
  },
  WIZARD_STEP_ONE_HOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:1',
    page: 'main:wps:donate:button:setup:wizard:hosted:1',
  },
  WIZARD_STEP_TWO_HOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:2',
    page: 'main:wps:donate:button:setup:wizard:hosted:2',
  },
  WIZARD_STEP_THREE_HOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:3',
    page: 'main:wps:donate:button:setup:wizard:hosted:3',
  },
  WIZARD_STEP_FOUR_HOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:hosted:4',
    page: 'main:wps:donate:button:setup:wizard:hosted:4',
  },
  WIZARD_STEP_ONE_UNHOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:1',
    page: 'main:wps:donate:button:setup:wizard:unhosted:1',
  },
  WIZARD_STEP_TWO_UNHOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:2',
    page: 'main:wps:donate:button:setup:wizard:unhosted:2',
  },
  WIZARD_STEP_THREE_UNHOSTED_PAGE: {
    pgrp: 'main:wps:donate:button:setup:wizard:unhosted:3',
    page: 'main:wps:donate:button:setup:wizard:unhosted:3',
  },
};

const DEFAULT_FPTI_DATA = {
  component: 'donatenodeweb',
  product: 'donate',
  account_type: getUserType(),
  encr_cust_id: getEncryptedAccountNum(), // check if this is generated automatically
  isLoggedIn: isLoggedInUser(),
  ...window?.fpti,
};

const SAVED_BUTTONS_DATA = {
  count,
  state,
};

const LANDING_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...PAGE_SPEC.LANDING_PAGE,
};

const MANAGE_PAGE_DEFAULT_DATA = {
  ...SAVED_BUTTONS_DATA,
  ...DEFAULT_FPTI_DATA,
  ...PAGE_SPEC.MANAGE_PAGE,
};

const FLOW = {
  flow: isDonateButton() ? 'button' : 'link',
};

const CONFIGURE_BUTTON_PAGE_DEFAULT_DATA = {
  step: 1,
  ...DEFAULT_FPTI_DATA,
  ...FLOW,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_ONE_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_ONE_UNHOSTED_PAGE }),
};

const EXPERIENCE_SETUP_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...FLOW,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_TWO_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_TWO_UNHOSTED_PAGE }),
};

const SET_AMOUNT_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...FLOW,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_THREE_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_THREE_UNHOSTED_PAGE }),
};

const ADVANCED_SETTINGS_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...FLOW,
  ...(isHosted
    ? { ...PAGE_SPEC.WIZARD_STEP_FOUR_HOSTED_PAGE }
    : { ...PAGE_SPEC.WIZARD_STEP_FOUR_UNHOSTED_PAGE }),
};

const OVERVIEW_PAGE_DEFAULT_DATA = {
  ...DEFAULT_FPTI_DATA,
  ...FLOW,
  ...PAGE_SPEC.OVERVIEW_PAGE,
};

const CLICK = {
  e: 'cl',
};

const IMPRESSION = {
  e: 'im',
};

const ERROR = {
  e: 'err',
};

const ACTIVITY = {
  e: 'ac',
};

export const FPTI_SPEC = {
  LANDING_PAGE_IM: {
    event_name: 'donate_setup_intro_screen_shown',
    data: {
      ...IMPRESSION,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_LOGIN_CL: {
    event_name: 'donate_setup_intro_login_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_SIGNUP_CL: {
    event_name: 'donate_setup_intro_sign_up_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_HEADER_LOGIN_CL: {
    event_name: 'donate_setup_intro_header_login_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  LANDING_PAGE_HEADER_SIGNUP_CL: {
    event_name: 'donate_setup_intro_header_sign_up_pressed',
    data: {
      ...CLICK,
      ...LANDING_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_IM: {
    event_name: 'donate_setup_manage_screen_shown',
    data: {
      ...IMPRESSION,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_CREATE_LINK_CL: {
    event_name: 'donate_setup_manage_create_link_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_CREATE_BUTTON_CL: {
    event_name: 'donate_setup_manage_create_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_MANAGE_BUTTON_CL: {
    event_name: 'donate_setup_manage_saved_button_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  MANAGE_PAGE_FEEDBACK_BUTTON_CL: {
    event_name: 'donate_setup_manage_feedback_pressed',
    data: {
      ...CLICK,
      ...MANAGE_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_IM: {
    event_name: 'donate_setup_configure_button_screen_shown',
    data: {
      ...IMPRESSION,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_configure_button_continue_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CANCEL_CL: {
    event_name: 'donate_setup_configure_button_cancel_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_configure_button_nav_back_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_configure_button_feedback_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_CHANGE_LOGO_CL: {
    event_name: 'donate_setup_configure_button_update_image_pressed',
    data: {
      ...CLICK,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  CONFIGURE_BUTTON_PAGE_LOGO_UPDATED: {
    event_name: 'donate_setup_configure_button_image_updated',
    data: {
      ...ACTIVITY,
      ...CONFIGURE_BUTTON_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_IM: {
    event_name: 'donate_setup_experience_setup_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_CANCEL_CL: {
    event_name: 'donate_setup_experience_setup_cancel_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_experience_setup_continue_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_BACK_CL: {
    event_name: 'donate_setup_experience_setup_back_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_experience_setup_nav_back_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_experience_setup_feedback_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_experience_setup_tab_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_UPLOAD_LOGO_CL: {
    event_name: 'donate_setup_experience_upload_logo_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_IM: {
    event_name: 'donate_setup_upload_logo_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BUTTON_IMAGE_SAVE_CL: {
    event_name: 'donate_setup_upload_button_image_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_SAVE_CL: {
    event_name: 'donate_setup_upload_logo_save_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_IMAGE_UPDATED: {
    event_name: 'donate_setup_upload_logo_image_updated',
    data: {
      ...ACTIVITY,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_CHECKBOX_CL: {
    event_name: 'donate_setup_upload_logo_checkbox_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_LOGO_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_upload_logo_dismiss_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  EXPERIENCE_SETUP_PAGE_UPLOAD_BACKGOURND_IMG_CL: {
    event_name: 'donate_setup_experience_upload_img_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGOURND_IMG_MODAL_IM: {
    event_name: 'donate_setup_upload_img_screen_shown',
    data: {
      ...IMPRESSION,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGOURND_IMG_MODAL_SAVE_CL: {
    event_name: 'donate_setup_upload_img_save_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGOURND_IMG_MODAL_IMAGE_UPDATED: {
    event_name: 'donate_setup_upload_img_img_updated',
    data: {
      ...ACTIVITY,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  UPLOAD_BACKGOURND_IMG_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_upload_img_dismiss_pressed',
    data: {
      ...CLICK,
      ...EXPERIENCE_SETUP_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_IM: {
    event_name: 'donate_setup_set_amount_screen_shown',
    data: {
      ...IMPRESSION,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_CONTINUE_CL: {
    event_name: 'donate_setup_set_amount_continue_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_FINISH_CL: {
    event_name: 'donate_setup_set_amount_finish_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_BACK_CL: {
    event_name: 'donate_setup_set_amount_back_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_set_amount_nav_back_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_set_amount_feedback_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  SET_AMOUNT_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_set_amount_tab_pressed',
    data: {
      ...CLICK,
      ...SET_AMOUNT_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_IM: {
    event_name: 'donate_setup_advanced_settings_screen_shown',
    data: {
      ...IMPRESSION,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_FINISH_CL: {
    event_name: 'donate_setup_advanced_settings_finish_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_BACK_CL: {
    event_name: 'donate_setup_advanced_settings_back_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_NAV_BACK_CL: {
    event_name: 'donate_setup_advanced_settings_nav_back_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_advanced_settings_feedback_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  ADVANCED_SETTINGS_PAGE_PREVIEW_TAB_CL: {
    event_name: 'donate_setup_advanced_settings_tab_pressed',
    data: {
      ...CLICK,
      ...ADVANCED_SETTINGS_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_IM: {
    event_name: 'donate_setup_success_screen_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_IM: {
    event_name: 'donate_setup_success_banner_shown',
    data: {
      ...IMPRESSION,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_SAVED_BUTTON_CL: {
    event_name: 'donate_setup_success_banner_donation_page_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SUCCESS_BANNER_DISMISS_CL: {
    event_name: 'donate_setup_success_banner_closed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_BACK_CL: {
    event_name: 'donate_setup_success_back_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_EDIT_NAME_CL: {
    event_name: 'donate_setup_success_edit_name_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SAVE_NAME_CL: {
    event_name: 'PLACEHOLDER_donate_setup_success_save_name_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_EDIT_CL: {
    event_name: 'donate_setup_success_edit_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_PREVIEW_CL: {
    event_name: 'donate_setup_success_preview_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_PREVIEW_DISMISS_CL: {
    event_name: 'donate_setup_success_preview_dismiss_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DUPLICATE_CL: {
    event_name: 'donate_setup_success_duplicate_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DELETE_CL: {
    event_name: 'donate_setup_success_delete_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_SHARE_CL: {
    event_name: 'donate_setup_success_share_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_COPY_LINK_CL: {
    event_name: 'donate_setup_success_copy_link_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_COPY_BUTTON_CL: {
    event_name: 'donate_setup_success_copy_button_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_DOWNLOAD_QRC_CL: {
    event_name: 'donate_setup_success_download_qrc_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  OVERVIEW_PAGE_FEEDBACK_CL: {
    event_name: 'donate_setup_success_feedback_pressed',
    data: {
      ...CLICK,
      ...OVERVIEW_PAGE_DEFAULT_DATA,
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_IM: {
    event_name: 'donate_setup_feature_education_screen_shown',
    data: {
      ...IMPRESSION,
      ...DEFAULT_FPTI_DATA,
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_SIDE_SCROLL_CL: {
    event_name: 'donate_setup_feature_education_side_scroll_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  PRODUCT_UPDATE_EXPERIENCE_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_feature_education_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ACTIONABLE_ERROR_MODAL_IM: {
    event_name: 'donate_setup_actionable_error_modal_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_REVIEW_CL: {
    event_name: 'donate_setup_actionable_error_edit_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_START_OVER_CL: {
    event_name: 'donate_setup_actionable_error_start_over_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ACTIONABLE_ERROR_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_actionable_error_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  FIELD_LEVEL_ERROR_IM: {
    event_name: 'donate_setup_input_validation_error_occurred',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ERROR_INTERACTION_IM: {
    event_name: 'donate_setup_failed_customer_interaction_occurred',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ERROR_INTERACTION_LINK_CL: {
    event_name: 'donate_setup_failed_customer_interaction_link_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ERROR_INTERACTION_DISMISS_CL: {
    event_name: 'donate_setup_failed_customer_interaction_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
    },
  },
  ALERT_MODAL_IM: {
    event_name: 'donate_setup_action_alert_modal_shown',
    data: {
      ...ERROR,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ALERT_MODAL_CONFIRM_CL: {
    event_name: 'donate_setup_action_alert_confirm_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ALERT_MODAL_CANCEL_CL: {
    event_name: 'donate_setup_action_alert_cancel_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
  ALERT_MODAL_DISMISS_CL: {
    event_name: 'donate_setup_action_alert_dismiss_pressed',
    data: {
      ...CLICK,
      ...DEFAULT_FPTI_DATA,
      ...FLOW,
    },
  },
};

export const ACTION = {
  EXTEND_SESSION: 'extend session',
  QUIT: 'quit creating donation page',
  DELETE: 'delete donation page',
};

export const SCREEN = {
  CONFIGURE_BUTTON: 'configure_button',
  EXPERIENCE_SETUP: 'experience_setup',
  SET_AMOUNT: 'set_amount',
  ADVANCED_SETTINGS: 'advanced_settings',
  MANAGE: 'manage',
  SUCCESS: 'success',
};
